import React, { useCallback, useEffect, useMemo, useState } from "react";
import BlockIcon from "@mui/icons-material/Block";
import PublishIcon from "@mui/icons-material/Publish";
import ShuffleIcon from "@mui/icons-material/Shuffle";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Logger from "../../../services/Logger";
import ConfirmDialog from "../../components/ConfirmDialog";
import CustomSnackbar from "../../components/CustomSnackbar";
import BorderHeader from "../../pzl_common/BorderHeader";
import StepPuzzleWordPanel from "../../pzl_common/board/StepPuzzleWordPanel";
import AdminWordWheelBoardService, {
    DISCARDED,
    DISABLED,
    DRAFT,
    PUBLISHED,
    READY_TO_REVIEW,
    REVIEWED,
} from "../board/AdminWordWheelBoardService";
import WordWheelCharacters from "../board/WordWheelCharacters";
import { WORD_WHEEL_GRID_SIZE } from "../board/wordWheelGridConstants";
import { extractTotalScore } from "../wordWheelDisplayUtils";
import {
    extractSeasonFromRow,
    hasAssignedPuzzleLevel,
    isJourneyPack,
} from "../wordWheelLevelUtils";
import AdminWordWheelService from "./AdminWordWheelService";
import WordWheelMissingClueFlag from "./WordWheelMissingClueFlag";
import WordWheelPublishDialog from "./WordWheelPublishDialog";
import WordWheelSwitchLevelDialog from "./WordWheelSwitchLevelDialog";

const GRID_SIZE = WORD_WHEEL_GRID_SIZE;
const PACK_NAVIGATOR_PATH = "/admin/puzzle/word_wheel_pack";
const TEMPLATE_LIST_PATH = "/admin/puzzle/word_wheel_tpl";
const PUBLISHABLE_STATES = new Set([DRAFT, READY_TO_REVIEW, REVIEWED]);

function findCharacter(row, col, filledCoordinates) {
    if (!filledCoordinates) return "";
    for (const coord of filledCoordinates) {
        const matchingPosition = coord.positions?.find(
            (p) => p.row === row && p.col === col
        );
        if (matchingPosition?.value) {
            return matchingPosition.value.toUpperCase();
        }
    }
    return "";
}

function initializeSquare(filledCoordinates) {
    const newSquare = Array.from({ length: GRID_SIZE }, () =>
        Array.from({ length: GRID_SIZE }, () => "")
    );
    for (let row = 0; row < GRID_SIZE; row++) {
        for (let col = 0; col < GRID_SIZE; col++) {
            newSquare[row][col] = findCharacter(row, col, filledCoordinates);
        }
    }
    return newSquare;
}

export default function AdminWordWheelTplDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const backPath = location.state?.backTo === PACK_NAVIGATOR_PATH
        ? PACK_NAVIGATOR_PATH
        : TEMPLATE_LIST_PATH;
    const [tpl, setTpl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionLoading, setActionLoading] = useState(null);
    const [fillingClues, setFillingClues] = useState(false);
    const [layoutVersion, setLayoutVersion] = useState(0);
    const [switchLevelOpen, setSwitchLevelOpen] = useState(false);
    const [publishOpen, setPublishOpen] = useState(false);
    const [disableOpen, setDisableOpen] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");

    const showSnackbar = (message, severity = "success") => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    const reloadTemplate = useCallback(async () => {
        const data = await AdminWordWheelService.requestTemplate(id);
        setTpl(data);
        return data;
    }, [id]);

    useEffect(() => {
        if (!id) {
            navigate(TEMPLATE_LIST_PATH);
            return;
        }
        let cancelled = false;
        setLoading(true);
        setError(null);
        AdminWordWheelService.requestTemplate(id)
            .then((data) => {
                if (!cancelled) {
                    setTpl(data);
                }
            })
            .catch((err) => {
                Logger.error("Failed to load word wheel template:", err);
                if (!cancelled) {
                    setError("Failed to load template. It may have been deleted.");
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [id, navigate]);

    const parsedFilledCoordinates = useMemo(() => {
        if (!tpl?.filledCoordinates) return [];
        try {
            return JSON.parse(tpl.filledCoordinates);
        } catch {
            return [];
        }
    }, [tpl?.filledCoordinates]);

    const totalScore = useMemo(() => extractTotalScore(tpl?.details), [tpl?.details]);
    const showTotalScore = totalScore != null
        && (tpl?.state === "REVIEWED" || tpl?.state === "PUBLISHED");
    const canEdit = tpl?.state !== DISCARDED;
    const hasLevel = hasAssignedPuzzleLevel(tpl);
    const journeyLocked = isJourneyPack(extractSeasonFromRow(tpl));
    const canPublish = Boolean(tpl)
        && PUBLISHABLE_STATES.has(tpl.state)
        && !journeyLocked;
    const canDisable = Boolean(tpl) && tpl.state === PUBLISHED;

    const parsePuzzleGrid = useCallback(() => {
        if (!tpl?.filledCoordinates) return [];
        try {
            const filledCoordinates = JSON.parse(tpl.filledCoordinates);
            return initializeSquare(filledCoordinates);
        } catch (err) {
            Logger.error("Error parsing filled coordinates:", err);
            return [];
        }
    }, [tpl?.filledCoordinates]);

    const handleBack = () => {
        navigate(backPath);
    };

    const handlePublish = async (options) => {
        if (!tpl?.id) {
            return;
        }
        setActionLoading("publish");
        try {
            const existingSeason = extractSeasonFromRow(tpl);
            const season = options.season
                || (existingSeason && !isJourneyPack(existingSeason) ? existingSeason : "");
            const result = await AdminWordWheelService.promoteTemplatesToPublished(
                [tpl],
                { ...options, season },
            );
            if (result.failed.length > 0) {
                showSnackbar(result.failed[0]?.error || "Failed to publish puzzle.", "error");
                return;
            }
            if (result.skipped.length > 0 && result.succeeded.length === 0) {
                showSnackbar("This puzzle is already published.", "info");
                return;
            }
            await reloadTemplate();
            showSnackbar("Puzzle published.");
            setPublishOpen(false);
        } catch (err) {
            Logger.error("Failed to publish word wheel template:", err);
            showSnackbar(err?.message || "Failed to publish puzzle.", "error");
        } finally {
            setActionLoading(null);
        }
    };

    const handleConfirmDisable = async () => {
        if (!tpl?.id) {
            return;
        }
        setActionLoading("disable");
        try {
            await AdminWordWheelBoardService.updateTplState(tpl.id, DISABLED);
            AdminWordWheelService.clearPublishedBySeasonCache();
            await reloadTemplate();
            showSnackbar("Puzzle disabled and removed from its pack.");
            setDisableOpen(false);
        } catch (err) {
            Logger.error("Failed to disable word wheel template:", err);
            showSnackbar(err?.message || "Failed to disable puzzle.", "error");
        } finally {
            setActionLoading(null);
        }
    };

    const handleShuffleLayout = async () => {
        if (!tpl?.id || !canEdit) {
            return;
        }
        setActionLoading("shuffle");
        try {
            const result = await AdminWordWheelService.shuffleLayout(tpl.id);
            const updated = result?.wordWheelTpl;
            if (updated) {
                setTpl(updated);
            } else {
                await reloadTemplate();
            }
            setLayoutVersion((n) => n + 1);
            showSnackbar("Layout shuffled.");
        } catch (err) {
            Logger.error("Failed to shuffle word wheel layout:", err);
            showSnackbar(err?.message || "Failed to shuffle layout.", "error");
        } finally {
            setActionLoading(null);
        }
    };

    const handleFillMissingClues = async () => {
        if (!tpl?.id || fillingClues) {
            return;
        }
        setFillingClues(true);
        try {
            const result = await AdminWordWheelService.fillDisplayCluesFromDictionary(tpl.id);
            const updated = result?.wordWheelTpl;
            const filled = Number(result?.filled) || 0;
            const stillMissing = Array.isArray(result?.stillMissing) ? result.stillMissing : [];
            if (updated) {
                setTpl(updated);
            } else {
                await reloadTemplate();
            }
            if (filled > 0 && stillMissing.length === 0) {
                showSnackbar(`Filled ${filled} clue(s) from the dictionary.`);
            } else if (filled > 0) {
                showSnackbar(
                    `Filled ${filled} clue(s). Still missing: ${stillMissing.join(", ")}.`,
                    "warning"
                );
            } else if (stillMissing.length > 0) {
                showSnackbar(
                    `No dictionary meanings found for: ${stillMissing.join(", ")}.`,
                    "warning"
                );
            } else {
                showSnackbar("All words already have clues.", "info");
            }
        } catch (err) {
            Logger.error("Failed to fill word wheel clues from dictionary:", err);
            showSnackbar(err?.message || "Failed to fill clues from the dictionary.", "error");
        } finally {
            setFillingClues(false);
        }
    };

    const handleSwitchLevel = async (puzzleLevel) => {
        if (!tpl?.id) {
            return;
        }
        setActionLoading("switch");
        try {
            const result = await AdminWordWheelService.switchPuzzleLevel(tpl.id, puzzleLevel);
            await reloadTemplate();
            setSwitchLevelOpen(false);
            const shifted = result?.shifted ?? 0;
            showSnackbar(
                shifted > 0
                    ? `Level set to ${puzzleLevel}. ${shifted} other puzzle(s) shifted.`
                    : `Level set to ${puzzleLevel}.`
            );
        } catch (err) {
            Logger.error("Failed to switch word wheel level:", err);
            showSnackbar(err?.message || "Failed to switch level.", "error");
        } finally {
            setActionLoading(null);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 240 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    if (!tpl) {
        return null;
    }

    return (
        <Box sx={{ p: 2, maxWidth: 1200, mx: "auto" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <BorderHeader
                        onBack={handleBack}
                        puzzle={tpl}
                        puzzleType="WORD_WHEEL"
                    />
                    <Box sx={{ mt: 1 }}>
                        <WordWheelMissingClueFlag
                            row={tpl}
                            filling={fillingClues}
                            onFill={handleFillMissingClues}
                        />
                    </Box>
                </Box>
                {canEdit && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2, flexShrink: 0, flexWrap: "wrap" }}>
                        {(canPublish || (tpl?.state !== PUBLISHED && journeyLocked)) && (
                            <Tooltip
                                title={
                                    journeyLocked
                                        ? "Season 2026 cannot be published from here"
                                        : "Publish this puzzle"
                                }
                            >
                                <span>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={() => setPublishOpen(true)}
                                        disabled={Boolean(actionLoading) || !canPublish}
                                        startIcon={
                                            actionLoading === "publish"
                                                ? <CircularProgress size={16} color="inherit" />
                                                : <PublishIcon />
                                        }
                                        sx={{ textTransform: "none", minHeight: 40 }}
                                    >
                                        Publish
                                    </Button>
                                </span>
                            </Tooltip>
                        )}
                        {canDisable && (
                            <Tooltip title="Withdraw this published puzzle">
                                <span>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        onClick={() => setDisableOpen(true)}
                                        disabled={Boolean(actionLoading)}
                                        startIcon={
                                            actionLoading === "disable"
                                                ? <CircularProgress size={16} color="inherit" />
                                                : <BlockIcon />
                                        }
                                        sx={{ textTransform: "none", minHeight: 40 }}
                                    >
                                        Disable
                                    </Button>
                                </span>
                            </Tooltip>
                        )}
                        <Tooltip title="Shuffle word placement on the grid">
                            <span>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={handleShuffleLayout}
                                    disabled={Boolean(actionLoading)}
                                    startIcon={
                                        actionLoading === "shuffle"
                                            ? <CircularProgress size={16} color="inherit" />
                                            : <ShuffleIcon />
                                    }
                                    sx={{ textTransform: "none", minHeight: 40 }}
                                >
                                    Shuffle layout
                                </Button>
                            </span>
                        </Tooltip>
                        <Tooltip title={hasLevel ? "Move this puzzle to another level and shift neighbors" : "This puzzle has no play level"}>
                            <span>
                                <Button
                                    variant="outlined"
                                    onClick={() => setSwitchLevelOpen(true)}
                                    disabled={Boolean(actionLoading) || !hasLevel}
                                    startIcon={<SwapVertIcon />}
                                    sx={{ textTransform: "none", minHeight: 40 }}
                                >
                                    Switch level
                                </Button>
                            </span>
                        </Tooltip>
                    </Box>
                )}
            </Box>

            <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: "#3BA666", mb: 1.5 }}>
                    Word Wheel
                </Typography>
                <Grid container spacing={1.5} alignItems="flex-start">
                    <Grid size={{ xs: 12, sm: showTotalScore ? 6 : 12 }}>
                        <WordWheelCharacters
                            embedded
                            compact
                            filledCoordinates={parsedFilledCoordinates}
                            wordsInUse={tpl.wordsInUse}
                        />
                    </Grid>
                    {showTotalScore && (
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "#3BA666", mb: 0.75 }}>
                                Total Score
                            </Typography>
                            <Typography variant="h6" color="secondary.main" sx={{ fontWeight: 700, fontSize: "1.125rem" }}>
                                {totalScore.toLocaleString()}
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            </Paper>

            <StepPuzzleWordPanel
                key={`${tpl.id}-${layoutVersion}-${tpl.updatedAt || ""}`}
                puzzle={tpl}
                parsePuzzleGrid={parsePuzzleGrid}
                enableScatterLayout
                enableRemoveWord
                onRefresh={() => {
                    setLayoutVersion((value) => value + 1);
                    reloadTemplate().catch((err) => {
                        Logger.error("Failed to reload word wheel template after scatter:", err);
                    });
                }}
                onNotify={(severity, message) => showSnackbar(message, severity)}
            />

            <WordWheelPublishDialog
                open={publishOpen}
                onClose={() => !actionLoading && setPublishOpen(false)}
                onConfirm={handlePublish}
                templates={tpl ? [tpl] : []}
                language={tpl?.language || "english"}
                loading={actionLoading === "publish"}
            />
            <WordWheelSwitchLevelDialog
                open={switchLevelOpen}
                onClose={() => !actionLoading && setSwitchLevelOpen(false)}
                onConfirm={handleSwitchLevel}
                template={tpl}
                loading={actionLoading === "switch"}
            />
            <ConfirmDialog
                open={disableOpen}
                setOpen={setDisableOpen}
                title="Confirm Disable Puzzle"
                message="Disable this puzzle? It will be withdrawn and removed from its pack. Scheduled for removal in 7 days (e.g. 6 days, 23 hours)."
                targetName={tpl?.title}
                targetId={tpl?.id}
                callback={handleConfirmDisable}
                okText="Disable"
            />

            <CustomSnackbar
                open={snackbarOpen}
                setOpen={setSnackbarOpen}
                severity={snackbarSeverity}
                message={snackbarMessage}
            />
        </Box>
    );
}
